// ============================================================================
// UN RÉGLAGE DE MAGASIN À L'ÉCRAN — sa lecture, son enregistrement, sa ligne dans Paramètres.
//
// UTILITÉ : deux écrans règlent le magasin. « Paramètres » porte le catalogue entier, par onglets ; le
// détail d'une INTÉGRATION porte, dans la carte de son connecteur, les seuls réglages qu'il commande
// (décision utilisateur : un magasin sans wallet n'a pas à traîner un menu inutile dans Paramètres). Les
// deux lisent la même table et écrivent par la même route : la lecture, le brouillon, l'enregistrement
// et les listes servies par un tiers sont écrits ICI une fois — deux copies auraient divergé au premier
// correctif (Forme A).
//
// ⚠️ LA LIGNE (`SettingRow`) EST CELLE DE PARAMÈTRES SEULEMENT : la carte d'un connecteur dessine la sienne
// (le wallet a un menu de gabarits avec pastille de couleur et « déjà pris »), que cette ligne ne sait pas rendre.
//
// ⚠️ LES MESURES DE LA LIGNE SONT CELLES DE LA MAQUETTE DE « PARAMÈTRES » — `parametres-maquette.test.ts`
// les lit dans le dessin et les cherche ici.
// ============================================================================
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Icon, showToast, Tooltip } from '@orgazone/ui-web';
import { switchOn, type SwitchName } from '@orgazone/sync-contract';
import { api, type StoreSettingRow } from '../api';
import { GOUVERNANCE, gouvernanceDe, type ReglageParam, type ReglageVue, type SourceListe } from './parametres-modele';
import { servedListFrom, servedListsEffectKey, type ServedList, type ServedLists } from './served-lists';
import { t } from '../i18n';

// ---------------------------------------------------------------------------
// LES LISTES SERVIES PAR UN TIERS
// ---------------------------------------------------------------------------

/**
 * QUI SERT CHAQUE SOURCE. ⚠️ Un `Record` sur l'union des sources : ajouter une source au modèle sans
 * dire ici qui la sert ne compile pas — sinon la nouvelle liste resterait « Chargement… » à jamais.
 */
const LIST_PROVIDERS: Record<SourceListe, () => Promise<ServedList>> = {
  wallet_coupon_templates: () => api.integrationWalletCouponTemplates().then(servedListFrom),
};

/**
 * LES LISTES SERVIES PAR UN TIERS — demandées UNE FOIS, et seulement si un réglage montré en réclame.
 * ⚠️ 🔴 ON NE DEMANDE RIEN QUAND AUCUN RÉGLAGE N'EN A BESOIN : cet appel traverse le connecteur
 *    jusque chez le fournisseur, et le payer à l'ouverture d'un écran qui ne l'affiche pas serait un
 *    aller-retour réseau pour personne. `enabled` = faux tant qu'aucun magasin n'est actif.
 * ⚠️ La dépendance de l'effet est la LISTE DES SOURCES, pas le tableau de réglages : un appelant qui
 *    en construit un neuf à chaque rendu redemanderait sinon la liste à chaque rendu.
 * ⚠️ 🔴 ET `reloadKey` : ELLE SE RELIT QUAND LA CONNEXION SE RELIT — clé posée, test, changement de magasin.
 *    Lue une seule fois, elle affichait l'erreur d'avant la clé à côté d'une connexion « établie ».
 */
export function useServedLists(settings: readonly ReglageVue[], enabled = true, reloadKey = ''): ServedLists {
  const [lists, setLists] = useState<ServedLists>(() => emptyServedLists());
  const effectKey = servedListsEffectKey(settings, enabled, reloadKey);
  useEffect(() => {
    if (!effectKey) return;
    let alive = true;
    const sources = effectKey.slice(0, effectKey.indexOf('|')).split(',') as SourceListe[];
    // Relue : on repasse par « Chargement… » plutôt que de laisser l'ancienne réponse passer pour la nouvelle.
    setLists((p) => { const n = { ...p }; for (const s of sources) n[s] = null; return n; });
    for (const source of sources) {
      LIST_PROVIDERS[source]()
        .catch((e: unknown): ServedList => ({ options: [], error: e instanceof Error ? e.message : String(e) }))
        .then((served) => { if (alive) setLists((p) => ({ ...p, [source]: served })); });
    }
    return () => { alive = false; };
  }, [effectKey]);
  return lists;
}

function emptyServedLists(): ServedLists {
  const empty = {} as ServedLists;
  for (const source of Object.keys(LIST_PROVIDERS) as SourceListe[]) empty[source] = null;
  return empty;
}

// ---------------------------------------------------------------------------
// LE BROUILLON ET SON ENREGISTREMENT
// ---------------------------------------------------------------------------

/**
 * Le brouillon après une saisie.
 * ⚠️ REVENIR À LA VALEUR D'ORIGINE RETIRE LA MODIFICATION : on n'écrit pas au journal un réglage que
 * personne n'a changé — ce serait une révision de plus à descendre sur toute la flotte, pour rien.
 */
export function nextDraft(draft: Record<string, string>, name: string, value: string, origin: string): Record<string, string> {
  const next = { ...draft };
  if (value === origin) delete next[name]; else next[name] = value;
  return next;
}

/** Ce qu'un écran de réglages tient entre deux enregistrements. */
export interface StoreSettingsDraft {
  /** Les lignes servies par le catalogue du magasin — `null` tant qu'elles ne sont pas lues. */
  rows: StoreSettingRow[] | null;
  /** Le motif RÉEL quand elles n'ont pas pu être lues. */
  error: string | null;
  byName: Map<string, StoreSettingRow>;
  draft: Record<string, string>;
  /** Les noms modifiés, non encore enregistrés. */
  changed: string[];
  busy: boolean;
  set: (name: string, value: string, origin: string) => void;
  /** Enregistre le brouillon ; vrai s'il est parti, faux s'il a été refusé — le motif est au toast. */
  save: () => Promise<boolean>;
  /**
   * Enregistre CES valeurs tout de suite, sans passer par le brouillon — le geste d'un interrupteur ou
   * d'un choix qui s'applique au clic. Vrai s'il est parti ; le succès se dit avec `success`, le refus
   * avec le motif du serveur.
   */
  commit: (values: Record<string, string>, success: string) => Promise<boolean>;
}

/**
 * LES RÉGLAGES DU MAGASIN, LEUR BROUILLON ET LEUR ENREGISTREMENT — le même chemin pour tout écran.
 * ⚠️ `enabled` = faux tant qu'aucun magasin n'est actif : la route lit la base DU magasin, et
 *    l'interroger sans magasin ne rendrait qu'un refus.
 */
export function useStoreSettingsDraft(enabled = true): StoreSettingsDraft {
  const [rows, setRows] = useState<StoreSettingRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    api.storeSettings()
      .then((r) => { if (alive) { setRows(r.settings); setError(null); } })
      .catch((e: unknown) => { if (alive) setError(e instanceof Error ? e.message : String(e)); });
    return () => { alive = false; };
  }, [enabled]);

  const byName = useMemo(() => new Map((rows ?? []).map((r) => [r.name, r])), [rows]);
  const changed = Object.keys(draft);
  const set = useCallback((name: string, value: string, origin: string) => setDraft((d) => nextDraft(d, name, value, origin)), []);

  const save = useCallback(async () => {
    const n = Object.keys(draft).length;
    if (!n || busy) return false;
    setBusy(true);
    try {
      const r = await api.saveStoreSettings(draft);
      setRows(r.settings);
      setDraft({});
      showToast(t('{n} réglage enregistré — il descendra aux caisses au prochain cycle.', { n }), 'ok', 8000);
      return true;
    } catch (e) {
      // ⚠️ LE MOTIF DU SERVEUR, jamais « erreur » : un refus de validation dit QUEL réglage il refuse.
      showToast(t('Enregistrement refusé : {message}', { message: e instanceof Error ? e.message : String(e) }), 'err', 10000);
      return false;
    } finally { setBusy(false); }
  }, [draft, busy]);

  /**
   * ⚠️ L'ÉCRAN NE BASCULE QU'APRÈS LA RÉPONSE : les lignes affichées sont celles que le serveur RENVOIE.
   *    Un interrupteur qui basculerait au clic resterait allumé sur un refus — l'écran annoncerait un
   *    réglage que le serveur n'a pas écrit (§23.0).
   */
  const commit = useCallback(async (values: Record<string, string>, success: string) => {
    if (busy) return false;
    setBusy(true);
    try {
      const r = await api.saveStoreSettings(values);
      setRows(r.settings);
      showToast(success, 'ok', 6000);
      return true;
    } catch (e) {
      showToast(t('Enregistrement refusé : {message}', { message: e instanceof Error ? e.message : String(e) }), 'err', 10000);
      // ⚠️ ON RELIT CE QUE LE SERVEUR TIENT : un refus veut dire qu'un autre a pu écrire entre-temps (le gabarit
      //    qu'on croyait libre), et une panne réseau ne dit pas si l'écriture a eu lieu. Best-effort assumé :
      //    si la relecture échoue aussi, l'écran garde l'état d'avant et le toast a déjà dit l'échec.
      api.storeSettings().then((r) => setRows(r.settings)).catch(() => undefined);
      return false;
    } finally { setBusy(false); }
  }, [busy]);

  return { rows, error, byName, draft, changed, busy, set, save, commit };
}

// ---------------------------------------------------------------------------
// LA LIGNE D'UN RÉGLAGE, ET SES DEUX CONTRÔLES
// ---------------------------------------------------------------------------

/** La pastille des modifications en attente — la place de la « révision » du dessin, occupée par ce qu'on sait. */
export function PendingChangesPill({ count }: { count: number }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 34, padding: '0 12px', borderRadius: 'var(--r-pill)', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 'var(--t-dense)', fontWeight: 600, whiteSpace: 'nowrap' }}>
      <span style={{ width: 7, height: 7, borderRadius: 'var(--r-pill)', background: 'var(--amber)' }} />
      {t('{n} modification non enregistrée', { n: count })}
    </span>
  );
}

/**
 * UN INTERRUPTEUR TEL QU'IL S'AFFICHE, et l'origine à laquelle comparer un clic.
 *
 * ⚠️ JAMAIS RÉGLÉ N'EST PAS ÉTEINT : absent, il vaut le défaut que la caisse applique (contrat). L'afficher
 *    éteint montrerait au siège le contraire de ce que fait la caisse.
 * ⚠️ L'ORIGINE EST L'ÉTAT EFFECTIF, écrit comme la caisse l'écrit : revenir à l'état affiché au chargement
 *    retire la modification, au lieu d'inscrire au journal un « 1 » que personne n'a décidé.
 */
export function switchView(name: SwitchName, stored: string | null, input: string | undefined): { on: boolean; origin: string } {
  return { on: switchOn(name, input ?? stored), origin: switchOn(name, stored) ? '1' : '0' };
}

/** Une ligne — relevé : filet EN HAUT, écart 14, colonne d'action à 170 px alignée à droite. */
export function SettingRow({ view, state, input, editing, onEdit, onClose, onChange }: {
  view: ReglageParam; state: StoreSettingRow;
  /** La saisie en cours (le brouillon) — `undefined` = intacte. */
  input: string | undefined;
  editing: boolean; onEdit: () => void; onClose: () => void;
  onChange: (name: string, value: string, origin: string) => void;
}) {
  const governance = gouvernanceDe(view.name, state.scope);
  const locked = governance === 'verrouille';
  const origin = state.value ?? '';
  const value = input ?? origin;
  const modified = input !== undefined;
  const g = GOUVERNANCE[governance];
  const sw = view.forme === 'toggle' ? switchView(view.name, state.value, input) : null;

  return (
    <div style={{
      display: 'flex', alignItems: 'center', gap: 14, padding: '12px 0',
      borderTop: '1px solid var(--ink-100)', flexWrap: 'wrap',
    }}>
      <span style={{ flex: 1, minWidth: 190 }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
          <span style={{ fontSize: 'var(--t-title)', fontWeight: 600, color: 'var(--ink-800)' }}>{t(view.label)}</span>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, height: 19, padding: '0 8px', borderRadius: 'var(--r-pill)', background: g.bg, color: g.fg, fontSize: 'var(--t-eyebrow)', fontWeight: 700, whiteSpace: 'nowrap' }}>
            {t(g.label)}
          </span>
        </span>
        {/* La note du CATALOGUE : elle vit au contrat, l'écran ne la réécrit pas. */}
        <span style={{ display: 'block', fontSize: 'var(--t-note)', color: 'var(--ink-500)', marginTop: 2, lineHeight: 1.5 }}>{t(state.note)}</span>
      </span>

      <span style={{ flexShrink: 0, display: 'flex', justifyContent: 'flex-end', minWidth: 170 }}>
        {sw ? (
          <SettingSwitch on={sw.on} disabled={locked} label={t(view.label)}
            onToggle={() => onChange(view.name, sw.on ? '0' : '1', sw.origin)} />
        ) : editing && !locked ? (
          <input
            autoFocus
            type={view.forme === 'secret' ? 'password' : 'text'}
            value={view.forme === 'secret' ? (input ?? '') : value}
            onChange={(e) => onChange(view.name, e.target.value, view.forme === 'secret' ? '' : origin)}
            onBlur={onClose}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') onClose(); }}
            placeholder={view.forme === 'secret' ? (state.pose ? t('•••••••• (enregistré)') : t('aucun mot de passe')) : state.value === null ? t('valeur par défaut') : ''}
            style={{
              height: 34, padding: '0 12px', borderRadius: 'var(--r-ctrl)', width: 170,
              background: 'var(--canvas)', border: `1px solid ${modified ? 'var(--amber)' : 'var(--ink-200)'}`,
              fontFamily: view.mono ? 'var(--font-mono)' : 'inherit',
              fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)', outline: 'none', boxSizing: 'border-box',
            }} />
        ) : (
          /* La PASTILLE du dessin : lecture au repos, et c'est le clic qui ouvre la saisie. */
          <Tooltip
            label={locked ? t('Réglage verrouillé') : undefined}
            content={locked
              ? t('Fixé par l’éditeur : il engage la certification ou l’identité fiscale de la caisse, et ne se change pas depuis un magasin.')
              : t('Cliquer pour modifier')}>
          <button type="button" onClick={locked ? undefined : onEdit} disabled={locked}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6, height: 34, padding: '0 12px',
              borderRadius: 'var(--r-ctrl)',
              background: locked ? 'var(--ink-50)' : modified ? 'var(--amber-bg)' : 'var(--paper)',
              border: `1px solid ${locked ? 'var(--ink-100)' : modified ? 'var(--amber)' : 'var(--ink-200)'}`,
              fontFamily: view.mono ? 'var(--font-mono)' : 'inherit',
              fontSize: 'var(--t-lead)', fontWeight: 600, color: 'var(--ink-800)',
              whiteSpace: 'nowrap', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis',
              cursor: locked ? 'not-allowed' : 'pointer',
            }}>
            {locked && <Icon name="lock" size={12} stroke={2.2} color="var(--ink-500)" />}
            {view.forme === 'secret'
              ? (state.pose ? '••••••••' : <span style={{ color: 'var(--ink-400)', fontWeight: 500 }}>{t('non renseigné')}</span>)
              : value !== '' ? value
                /* ⚠️ « Jamais réglé » ≠ « vide » : la caisse applique son défaut, et l'écran le dit. */
                : <span style={{ color: 'var(--ink-400)', fontWeight: 500 }}>{t('défaut de la caisse')}</span>}
          </button>
          </Tooltip>
        )}
      </span>
    </div>
  );
}

/** L'interrupteur du dessin : 38×22, curseur de 16 à 19 px (actif) ou 3 px (repos). */
function SettingSwitch({ on, disabled, onToggle, label }: {
  on: boolean; disabled: boolean; onToggle: () => void; label: string;
}) {
  return (
    <button type="button" role="switch" aria-checked={on} aria-label={label}
      onClick={disabled ? undefined : onToggle} disabled={disabled}
      style={{
        position: 'relative', width: 38, height: 22, border: 'none', padding: 0,
        borderRadius: 'var(--r-pill)', background: on ? 'var(--teal-500)' : 'var(--ink-300)',
        transition: 'background .15s', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.55 : 1,
      }}>
      <span style={{
        position: 'absolute', top: 3, left: on ? 19 : 3, width: 16, height: 16,
        borderRadius: 'var(--r-pill)', background: '#fff',
        boxShadow: '0 1px 3px rgba(12,14,16,.3)', transition: 'left .15s',
      }} />
    </button>
  );
}
